/**
 * Who a deployment says it is, read from its `/.well-known/featit` (F-0518, AC-FD3, AC-FD6).
 *
 * The first screen names a non-default deployment by this answer, never by its address, and the
 * secondary screen shows it before anything is stored. Each failure is its own outcome because each
 * one is a different next step: offline means check the network, unreachable the address, not-featit
 * the server, and certificate means the app will not trust it, full stop.
 *
 * ⛔ **The certificate outcome comes only from the desktop's native check.** In a webview a refused
 * connection, a missing host and an untrusted certificate are all the same `TypeError`, so a
 * browser read never claims to know which it was. The desktop asks `read_deployment_identity`
 * (`desktop/src-tauri/src/deployment_identity.rs`), which reads the failure's own rustls error.
 */
import { WELL_KNOWN_FEATIT_PATH, type IFeatitWellKnown } from '@featit/shared/app-setup';
import { isDesktopRuntime } from '@/desktop/desktopDetection';
import { readDeploymentIdentityNatively } from '@/desktop/desktopRuntimeLazy';
import type { NativeDeploymentIdentityAnswerType } from '@/desktop/desktopRuntime';

export const DEPLOYMENT_IDENTITY_FAILURES = {
  offline: 'offline',
  unreachable: 'unreachable',
  notFeatit: 'not-featit',
  certificate: 'certificate',
} as const;

export type DeploymentIdentityFailureType =
  (typeof DEPLOYMENT_IDENTITY_FAILURES)[keyof typeof DEPLOYMENT_IDENTITY_FAILURES];

export type DeploymentIdentityAnswerType =
  | { outcome: 'verified'; identity: IFeatitWellKnown }
  | { outcome: DeploymentIdentityFailureType };

/** Long enough for a slow deployment, short enough that a first screen never hangs. */
const BROWSER_TIMEOUT_MS = 8_000;

/** A document that is FEATit's, with the fields the first screen reads, or null. */
export function asFeatitIdentity(body: unknown): IFeatitWellKnown | null {
  if (!body || typeof body !== 'object') return null;
  const candidate = body as Partial<IFeatitWellKnown>;
  if (candidate.product !== 'featit') return null;
  if (typeof candidate.name !== 'string' || typeof candidate.origin !== 'string') return null;
  return candidate as IFeatitWellKnown;
}

/** The deployment's icon as an absolute address, or null when it names none that can be shown. */
export function deploymentIconUrl(identity: IFeatitWellKnown, address: string): string | null {
  if (!identity.iconUrl) return null;
  try {
    const url = new URL(identity.iconUrl, address);
    return url.protocol === 'https:' || url.protocol === 'http:' ? url.toString() : null;
  } catch {
    return null;
  }
}

function isOffline(): boolean {
  return typeof navigator !== 'undefined' && navigator.onLine === false;
}

async function readInBrowser(address: string): Promise<DeploymentIdentityAnswerType> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), BROWSER_TIMEOUT_MS);
  try {
    const response = await fetch(new URL(WELL_KNOWN_FEATIT_PATH, address).toString(), {
      headers: { accept: 'application/json' },
      cache: 'no-store',
      signal: controller.signal,
    });
    if (response.status >= 502 && response.status <= 504) return { outcome: 'unreachable' };
    const identity = response.ok ? asFeatitIdentity(await response.json().catch(() => null)) : null;
    return identity ? { outcome: 'verified', identity } : { outcome: 'not-featit' };
  } catch {
    return { outcome: 'unreachable' };
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Read `address`'s identity. Offline is answered without asking, because a request that can only
 * time out would hold the screen for eight seconds to say what the device already knows.
 */
export async function readDeploymentIdentity(
  address: string,
): Promise<DeploymentIdentityAnswerType> {
  if (isOffline()) return { outcome: 'offline' };
  if (!isDesktopRuntime()) return readInBrowser(address);
  let answer: NativeDeploymentIdentityAnswerType;
  try {
    answer = await readDeploymentIdentityNatively(address);
  } catch {
    return { outcome: 'unreachable' };
  }
  if (answer.outcome === 'verified') {
    const identity = asFeatitIdentity(answer.identity);
    return identity ? { outcome: 'verified', identity } : { outcome: 'not-featit' };
  }
  if (answer.outcome === 'invalid-address') return { outcome: 'unreachable' };
  return { outcome: answer.outcome };
}
